import { useEffect, useRef, useState } from "react";
import { Compass, GraduationCap, LineChart, RotateCcw, MessageSquarePlus, ScanLine, Target } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AI_CSS } from "@/components/ai/motion";
import Reveal from "@/components/ai/Reveal";
import { fmtMYT } from "@/lib/myt";
import AIAmbientBackdrop from "@/components/ai/AIAmbientBackdrop";
import AIIdentityHeader from "@/components/ai/AIIdentityHeader";
import AICreditMeter from "@/components/ai/AICreditMeter";
import AIActionDeck from "@/components/ai/AIActionDeck";
import AIComposer from "@/components/ai/AIComposer";
import AIProcessingRail from "@/components/ai/AIProcessingRail";
import AIOrbitalCore from "@/components/ai/AIOrbitalCore";
import AISignalArtifact from "@/components/ai/AISignalArtifact";
import AIAnalysisReport from "@/components/ai/AIAnalysisReport";
import AIResultStates from "@/components/ai/AIResultStates";
import AIHistoryDrawer from "@/components/ai/AIHistoryDrawer";

// Web AI is in a pre-launch "Coming Soon" state: no active chat, signal generation,
// or trading advice is rendered on the web. Backend data, history and admin settings
// are untouched — flipping this back only re-enables the existing UI.
const AI_WEB_COMING_SOON = true;

const QUICK_ACTIONS = [
  { key: "market_analysis", label: "Analyse Market", icon: LineChart, cost: "market_analysis", placeholder: "e.g. Analyse the current XAUUSD structure on the 4H timeframe…", image: false },
  { key: "signal", label: "Get Trading Signal", icon: Target, cost: "signal", placeholder: "Attach a current chart screenshot, then describe the instrument and timeframe…", image: true },
  { key: "chart_analysis", label: "Analyse My Chart", icon: ScanLine, cost: "chart_analysis", placeholder: "Attach your chart screenshot and tell me what you'd like to understand…", image: true },
  { key: "advice", label: "Ask For Trading Advice", icon: Compass, cost: "question", placeholder: "e.g. I'm in a drawdown — how should I think about my next trade?", image: false },
  { key: "learn", label: "Learn Trading", icon: GraduationCap, cost: "question", placeholder: "e.g. Explain liquidity sweeps like I'm new to trading…", image: false },
];

/** Market chips — instant conversation starters on the home state. */
const MARKET_CHIPS = [
  { label: "XAUUSD", tag: "GOLD", text: "Analyse XAUUSD on the M15 timeframe — structure, liquidity, momentum and session context." },
  { label: "BTCUSD", tag: "CRYPTO", text: "Analyse BTCUSD on the H1 timeframe — structure, liquidity and momentum." },
  { label: "FOREX MAJORS", tag: "FX", text: "Give me a structured read on the forex majors today — where is the cleanest structure?" },
  { label: "MARKET SCAN", tag: "SCAN", text: "Scan the market for me — which instruments currently offer the clearest structure and liquidity picture?" },
];

/** Popular prompts — prefill the composer; the member always confirms before credits are spent. */
const POPULAR_PROMPTS = [
  { label: "Analyse XAUUSD M15", action: "market_analysis", text: "Analyse XAUUSD on the M15 timeframe — structure, liquidity, momentum and session context." },
  { label: "Find a trading setup", action: "signal", text: "Find a high-quality trade setup on this chart — direction, entry zone, invalidation and target." },
  { label: "What's the current trend?", action: "market_analysis", text: "What is the current trend on XAUUSD — higher-timeframe bias, structure and momentum?" },
  { label: "Check key liquidity levels", action: "market_analysis", text: "Where is liquidity resting on XAUUSD — pools above and below, and the likely draw on liquidity?" },
  { label: "Explain this chart", action: "chart_analysis", text: "Explain this chart to me — structure, liquidity, momentum and what you'd watch next." },
];

/** Packs the live result into a compact key-value context block for follow-up questions. */
function packContext(r) {
  if (!r) return "";
  if (r.result_type === "signal") {
    const lines = [`Status: ${r.status || r.decision}`];
    if (r.instrument) lines.push(`Instrument: ${r.instrument}`);
    if (r.direction) lines.push(`Direction: ${r.direction}`);
    if (r.entry) lines.push(`Entry zone: ${r.entry}`);
    if (r.stop_loss != null) lines.push(`Stop loss: ${r.stop_loss}`);
    if (r.take_profit != null) lines.push(`Take profit: ${r.take_profit}`);
    if (r.risk_reward) lines.push(`Risk/reward: 1:${r.risk_reward}`);
    if (r.market_structure) lines.push(`Structure: ${r.market_structure}`);
    if (r.liquidity) lines.push(`Liquidity: ${r.liquidity}`);
    if (r.momentum) lines.push(`Momentum: ${r.momentum}`);
    if (r.reasoning) lines.push(`Reasoning: ${r.reasoning}`);
    if (r.confirmation) lines.push(`Confirmation pending: ${r.confirmation}`);
    return `[My current PlusMaker AI analysis:\n${lines.join("\n")}]`;
  }
  if (r.result_type === "analysis") {
    return `[My current PlusMaker AI analysis: ${(r.sections || []).map((s) => `${s.title}: ${s.body}`).join(" | ").slice(0, 1200)}]`;
  }
  if (r.result_type === "text") return `[Previous PlusMaker AI answer: ${(r.content || "").slice(0, 800)}]`;
  return "";
}

/** Compact session summary shown above the follow-up discussion — keeps the live
 *  setup visible while the member talks about it. */
function SessionSummary({ result }) {
  const status = result.status || result.decision;
  const rows = [
    ["Direction", result.direction],
    ["Entry Zone", result.entry],
    ["Stop Loss", result.stop_loss],
    ["Take Profit", result.take_profit],
    ["Risk / Reward", result.risk_reward ? `1 : ${result.risk_reward}` : null],
    ["Status", status],
  ].filter(([, v]) => v != null);
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border border-[#D4AF37]/25 bg-[#0A0C10] p-4" data-testid="ai-session-summary">
      <p className="text-[9px] font-mono tracking-[0.25em] text-[#E9C766] mb-3">AI SESSION SUMMARY</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
        {rows.map(([k, v]) => (
          <div key={k}>
            <span className="text-[8px] font-mono tracking-[0.18em] text-[#475569] block">{k.toUpperCase()}</span>
            <span className="text-xs font-semibold text-[#C7CDD9] font-mono">{String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MemberAI() {
  const [access, setAccess] = useState(null);
  const [action, setAction] = useState(null);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [resultKey, setResultKey] = useState(0);
  const [resultImage, setResultImage] = useState(null);
  const [resultMeta, setResultMeta] = useState(null); // {timeframe, at}
  const [thread, setThread] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/member/ai/access");
      setAccess(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    api.get("/member/ai/history").then((r) => setHistory(r.data.history)).catch(() => {});
  }, []);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (busy || !text.trim()) return;
    const isFollowUp = !!result && !action; // a plain question asked on top of a live result
    const question = text.trim();
    const tfMatch = question.match(/\b(M1|M5|M15|M30|H1|H4|D1|W1)\b/i);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("request_type", action ? action.cost : "question");
      const ctx = isFollowUp ? packContext(result) : "";
      fd.append("text", ctx ? `${ctx}\n\nFollow-up question: ${question}` : question);
      if (image) fd.append("image", image);
      const res = await api.post("/member/ai/request", fd, { timeout: 190000 });
      if (isFollowUp && res.data.result_type === "text") {
        setThread((t) => [...t, { role: "user", text: question }, { role: "ai", text: res.data.content }]);
      } else {
        setResult(res.data);
        setResultImage(imagePreview);
        setResultKey((k) => k + 1);
        setResultMeta({
          timeframe: tfMatch ? tfMatch[0].toUpperCase() : null,
          at: fmtMYT(new Date()),
        });
        setThread([]);
      }
      if (res.data.access) setAccess(res.data.access);
      api.get("/member/ai/history").then((r) => setHistory(r.data.history)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setText("");
    }
  };

  const newAnalysis = () => {
    setResult(null);
    setResultImage(null);
    setResultMeta(null);
    setImage(null);
    setImagePreview(null);
    setText("");
    setAction(null);
    setThread([]);
  };

  const followUp = () => {
    setAction(null);
    setImage(null);
    setImagePreview(null);
    document.querySelector('[data-testid="ai-composer-input"]')?.focus();
  };

  const pickPrompt = (chip) => {
    const qa = QUICK_ACTIONS.find((a) => a.key === chip.action);
    setAction(qa || null);
    setText(chip.text);
    setResult(null);
    setResultImage(null);
    setResultMeta(null);
    setThread([]);
    document.querySelector('[data-testid="ai-composer-input"]')?.focus();
  };

  const cost = action ? (access?.costs?.[action.cost] ?? 1) : (access?.costs?.question ?? 1);
  const showEvidence = resultImage && result && (result.result_type === "signal" || result.result_type === "analysis");
  const idle = !result && !busy;
  const sigStatus = result?.result_type === "signal" ? (result.status || result.decision) : null;
  const aiState = busy ? "analysing"
    : !result ? "online"
    : result.result_type === "signal"
      ? (sigStatus === "VALID SETUP" ? "ready" : ["WAIT FOR ENTRY", "WAIT FOR CONFIRMATION"].includes(sigStatus) ? "waiting" : "notrade")
      : "complete";

  return AI_WEB_COMING_SOON ? (
    <div className="max-w-xl mx-auto" data-testid="member-ai-page">
      <div className="card-lux p-10 text-center space-y-4" data-testid="ai-coming-soon">
        <p className="eyebrow-label">PLUSMAKER Lab</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">PlusMaker AI</h1>
        <span className="inline-block text-[10px] font-mono tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#E9C766]" data-testid="ai-coming-soon-badge">
          Coming Soon
        </span>
        <p className="text-sm text-[#8A93A6] leading-relaxed">
          PlusMaker AI is being prepared as a platform assistant for members. It is not currently available
          on the web at this time. We'll announce its launch in the member area.
        </p>
      </div>
    </div>
  ) : (
    <div className="relative max-w-5xl mx-auto space-y-8" data-testid="member-ai-page">
      <style>{AI_CSS}</style>
      <AIAmbientBackdrop />

      {/* ---------- Home / ASK — the AI is present before the first question ---------- */}
      {idle && (
        <div className="relative text-center pt-1 pb-1 space-y-5" data-testid="ai-home-state">
          <AIIdentityHeader state={aiState} />
          <Reveal delay={80}>
            <AIOrbitalCore size={140} state={aiState} />
          </Reveal>
          <Reveal delay={160}>
            <p className="text-sm text-[#8A93A6] tracking-wide">Real analysis. Real setups. No guessing.</p>
          </Reveal>
          <Reveal delay={220}>
            <div className="flex flex-wrap justify-center gap-2.5">
              {MARKET_CHIPS.map((chip) => (
                <button key={chip.label} onClick={() => pickPrompt({ action: "market_analysis", text: chip.text })} data-testid={`ai-market-chip-${chip.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="ai-press flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#101318]/80 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/55">
                  <span className="text-xs font-bold tracking-wider text-white">{chip.label}</span>
                  <span className="text-[8px] font-mono tracking-[0.2em] text-[#D4AF37]/70">{chip.tag}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      )}

      {/* ---------- Identity (slim) while analysing / viewing results ---------- */}
      {!idle && <AIIdentityHeader state={aiState} compact />}

      {access && !access.enabled && (
        <Reveal delay={120}>
          <div className="card-lux p-8 text-center text-sm text-[#8A93A6]" data-testid="ai-unavailable">
            PlusMaker AI is temporarily unavailable. Please check back shortly.
          </div>
        </Reveal>
      )}

      {access?.enabled && (
        <>
          {idle && (
            <Reveal delay={300}>
              <div className="max-w-xl mx-auto w-full" data-testid="ai-popular-prompts">
                <p className="text-[10px] font-mono tracking-[0.25em] text-[#64748B] mb-3 text-center">POPULAR PROMPTS</p>
                <div className="space-y-2">
                  {POPULAR_PROMPTS.map((p, i) => (
                    <button key={p.label} onClick={() => pickPrompt(p)} data-testid={`ai-prompt-${i}`}
                      className="ai-press w-full flex items-center justify-between rounded-xl border border-white/10 bg-[#101318]/70 px-4 py-3 hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/[0.05]">
                      <span className="text-sm text-[#C7CDD9]">{p.label}</span>
                      <span className="text-[#D4AF37]/60 text-xs">→</span>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <AIActionDeck actions={QUICK_ACTIONS} active={action} costs={access.costs} compact={!!(result || busy)}
            onSelect={(qa) => { setAction(qa); setResult(null); setResultImage(null); setResultMeta(null); setThread([]); }} />

          <AIComposer action={action} text={text} onText={setText} imagePreview={imagePreview}
            onPickImage={pickImage} onRemoveImage={() => { setImage(null); setImagePreview(null); }}
            onClearAction={() => { setAction(null); setImage(null); setImagePreview(null); }}
            onSubmit={submit} busy={busy} cost={cost} fileRef={fileRef} />

          {busy && <AIProcessingRail chartThumb={imagePreview} subject={result ? "Follow-up question" : (action ? action.label : "Your request")} />}

          {result && !busy && (
            <div data-testid="ai-result" key={resultKey}>
              {result.result_type === "signal" && (
                <AISignalArtifact result={result} chartThumb={showEvidence ? resultImage : null}
                  timeframe={resultMeta?.timeframe} analysedAt={resultMeta?.at} />
              )}
              {result.result_type === "analysis" && <AIAnalysisReport result={result} chartThumb={showEvidence ? resultImage : null} />}
              {["text", "needs_image", "limit", "unavailable", "error"].includes(result.result_type) && <AIResultStates result={result} />}

              {/* follow-up conversation — one intelligence system, signal stays in view */}
              {thread.length > 0 && (
                <div className="mt-6 space-y-4" data-testid="ai-thread">
                  {result.result_type === "signal" && <SessionSummary result={result} />}
                  <p className="text-[10px] font-mono tracking-[0.25em] text-[#64748B]">DISCUSSION · THIS ANALYSIS</p>
                  {thread.map((turn, i) => (
                    <div key={i} className={`ai-thread-in flex ${turn.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-thread-${turn.role}-${i}`}>
                      {turn.role === "ai" && (
                        <div className="h-8 w-8 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 flex items-center justify-center shrink-0 mr-3 mt-1">
                          <span className="text-[11px]" style={{ fontFamily: "Cinzel, serif", color: "#E9C766" }}>♛</span>
                        </div>
                      )}
                      <div className={turn.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-md border border-[#D4AF37]/35 bg-[#D4AF37]/[0.08] px-4 py-3"
                        : "max-w-[92%] rounded-2xl rounded-tl-md border border-white/10 bg-[#101318] px-5 py-4"}>
                        <p className={`text-sm leading-relaxed whitespace-pre-line ${turn.role === "user" ? "text-[#E9C766]" : "text-[#C7CDD9]"}`}>{turn.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Reveal delay={750}>
                <div className="flex gap-3 justify-center mt-5 flex-wrap">
                  <button onClick={followUp} data-testid="ai-followup-btn"
                    className="ai-press px-5 py-2 rounded-xl border border-[#D4AF37]/35 text-[#E9C766] text-xs font-bold tracking-widest hover:bg-[#D4AF37]/10 flex items-center gap-2">
                    <MessageSquarePlus size={14} /> ASK FOLLOW-UP
                  </button>
                  <button onClick={newAnalysis} data-testid="ai-new-analysis-btn"
                    className="ai-press px-5 py-2 rounded-xl border border-white/15 text-[#94A3B8] text-xs font-bold tracking-widest hover:bg-white/5 hover:text-white flex items-center gap-2">
                    <RotateCcw size={14} /> NEW ANALYSIS
                  </button>
                </div>
              </Reveal>
            </div>
          )}

          <AIHistoryDrawer history={history} show={showHistory} onToggle={() => setShowHistory(!showHistory)}
            onSelect={(h) => { setResult(h.result); setResultImage(null); setResultMeta(null); setResultKey((k) => k + 1); setThread([]); }} />
        </>
      )}

      {/* subscription status — compact, secondary by design (lowest visual priority) */}
      {access && <AICreditMeter access={access} />}
    </div>
  );
}
